import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Typography, Grid, Link, Divider, Stack,
  IconButton,
} from '@mui/material';
import {
  Email, LocationOn,
  Facebook, Instagram,
} from '@mui/icons-material';
import {
  CONTACT_EMAIL,
  WHATSAPP_NUMBER,
  FACEBOOK_URL,
  INSTAGRAM_URL,
  whatsAppHref,
} from '../constants/contact';

const COURIER_URL = process.env.REACT_APP_COURIER_URL || 'http://localhost:3000';
const DRIVER_URL = process.env.REACT_APP_DRIVER_URL || 'http://localhost:3001';

type FooterNavItem =
  | { label: string; href: string; external: true }
  | { label: string; pathname: '/'; hash: string }
  | { label: string; to: string };

const footerSections: Record<string, FooterNavItem[]> = {
  'Plataforma de estafetas': [
    { label: 'Gestão de estafetas', href: COURIER_URL, external: true },
    { label: 'Relatórios de parceiros', href: COURIER_URL, external: true },
    { label: 'Pagamentos SEPA', href: COURIER_URL, external: true },
    { label: 'Faturação', href: COURIER_URL, external: true },
    { label: 'Iniciar sessão', href: COURIER_URL, external: true },
    { label: 'Registo', href: `${COURIER_URL}/register`, external: true },
  ],
  'Plataforma de motoristas': [
    { label: 'Gestão de motoristas', href: DRIVER_URL, external: true },
    { label: 'Acompanhamento da frota', href: DRIVER_URL, external: true },
    { label: 'Pagamentos SEPA', href: DRIVER_URL, external: true },
    { label: 'Rendas a proprietários', href: DRIVER_URL, external: true },
    { label: 'Iniciar sessão', href: DRIVER_URL, external: true },
    { label: 'Registo', href: `${DRIVER_URL}/register`, external: true },
  ],
  Produto: [
    { label: 'Funcionalidades', pathname: '/', hash: '#features' },
    { label: 'Como funciona', pathname: '/', hash: '#how-it-works' },
    { label: 'Preços', pathname: '/', hash: '#pricing' },
    { label: 'Testemunhos', pathname: '/', hash: '#testimonials' },
    { label: 'Política de privacidade', to: '/privacy' },
    { label: 'Termos de utilização', to: '/terms' },
    { label: 'Política de cookies', to: '/cookies' },
  ],
};

function FooterNavLink({ item }: { item: FooterNavItem }) {
  const sx = {
    color: 'rgba(255,255,255,0.45)',
    fontSize: '0.85rem',
    transition: 'color 0.2s ease',
    cursor: 'pointer',
    '&:hover': { color: '#fff' },
  };

  if ('external' in item && item.external) {
    return (
      <Link href={item.href} underline="none" sx={sx}>
        {item.label}
      </Link>
    );
  }

  if ('hash' in item) {
    return (
      <Link
        component={RouterLink}
        to={{ pathname: item.pathname, hash: item.hash }}
        underline="none"
        sx={sx}
      >
        {item.label}
      </Link>
    );
  }

  if ('to' in item) {
    return (
      <Link component={RouterLink} to={item.to} underline="none" sx={sx}>
        {item.label}
      </Link>
    );
  }

  return null;
}

const contactLinkSx = {
  color: 'rgba(255,255,255,0.45)',
  fontSize: '0.8rem',
  '&:hover': { color: 'rgba(255,255,255,0.85)' },
};

export default function Footer() {
  const mailto = `mailto:${CONTACT_EMAIL}`;
  const waLink = whatsAppHref(WHATSAPP_NUMBER);

  return (
    <Box
      component="footer"
      sx={{
        background: '#071525',
        color: '#fff',
        pt: { xs: 7, md: 9 },
        pb: 4,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={5}>
          {/* Brand column */}
          <Grid item xs={12} md={4}>
            <Box component="img" src="/logo-white.svg" alt="NeoDrive" sx={{ height: 36, mb: 2.5 }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', lineHeight: 1.8, mb: 3, maxWidth: 300 }}>
              Plataforma completa de gestão para empresas de estafetas e operadores de frota na Europa.
              Automatize remunerações, gere pagamentos SEPA e envie faturas sem complicações.
            </Typography>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.1em',
                color: 'rgba(255,255,255,0.45)',
                mb: 1.5,
                textTransform: 'uppercase',
              }}
            >
              Contacto
            </Typography>
            <Stack spacing={1.2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <Box sx={{ color: 'rgba(255,255,255,0.35)' }}>
                  <Email sx={{ fontSize: 16 }} />
                </Box>
                <Link href={mailto} underline="none" sx={contactLinkSx}>
                  {CONTACT_EMAIL}
                </Link>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <Box
                  component="span"
                  sx={{ color: '#25D366', fontSize: 14, lineHeight: 1, display: 'flex', alignItems: 'center' }}
                  aria-hidden
                >
                  <WhatsAppGlyph />
                </Box>
                <Link href={waLink} target="_blank" rel="noopener noreferrer" underline="none" sx={contactLinkSx}>
                  WhatsApp {WHATSAPP_NUMBER}
                </Link>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                <Box sx={{ color: 'rgba(255,255,255,0.35)' }}>
                  <LocationOn sx={{ fontSize: 16 }} />
                </Box>
                <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.8rem' }}>
                  Lisboa, Portugal
                </Typography>
              </Box>
            </Stack>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '0.75rem',
                letterSpacing: '0.1em',
                color: 'rgba(255,255,255,0.45)',
                mb: 1.5,
                mt: 3,
                textTransform: 'uppercase',
              }}
            >
              Siga-nos
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                component="a"
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="NeoDrive no Facebook"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.45)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  width: 38,
                  height: 38,
                  '&:hover': {
                    color: '#fff',
                    borderColor: 'rgba(255,255,255,0.3)',
                    background: 'rgba(255,255,255,0.08)',
                  },
                }}
              >
                <Facebook fontSize="small" />
              </IconButton>
              <IconButton
                component="a"
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="NeoDrive no Instagram"
                size="small"
                sx={{
                  color: 'rgba(255,255,255,0.45)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  width: 38,
                  height: 38,
                  '&:hover': {
                    color: '#fff',
                    borderColor: 'rgba(255,255,255,0.3)',
                    background: 'rgba(255,255,255,0.08)',
                  },
                }}
              >
                <Instagram fontSize="small" />
              </IconButton>
            </Box>
          </Grid>

          {/* Link columns */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              {Object.entries(footerSections).map(([section, links]) => (
                <Grid item xs={12} sm={4} key={section}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.8rem',
                      letterSpacing: '0.08em',
                      color: 'rgba(255,255,255,0.5)',
                      mb: 2.5,
                      textTransform: 'uppercase',
                    }}
                  >
                    {section}
                  </Typography>
                  <Stack spacing={1.2}>
                    {links.map((link) => (
                      <FooterNavLink key={link.label} item={link} />
                    ))}
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', my: 5 }} />

        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'center', md: 'center' },
          gap: 2,
        }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem' }}>
            © {new Date().getFullYear()} NeoDrive. Todos os direitos reservados.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80' }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem' }}>
              Todos os sistemas operacionais
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
            {[
              { label: 'Política de privacidade', to: '/privacy' },
              { label: 'Termos de utilização', to: '/terms' },
              { label: 'Política de cookies', to: '/cookies' },
            ].map((item) => (
              <Link
                key={item.label}
                component={RouterLink}
                to={item.to}
                underline="none"
                sx={{
                  color: 'rgba(255,255,255,0.3)',
                  fontSize: '0.78rem',
                  '&:hover': { color: 'rgba(255,255,255,0.65)' },
                }}
              >
                {item.label}
              </Link>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

/** Simple WhatsApp-style glyph for contact row (no extra dependency). */
function WhatsAppGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.435 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}
